import { useEffect, useState } from "react";
import type { DocumentMeta, FolderMeta } from "@quill/shared";
import { ApiError, api } from "../api";
import { Dialog, PromptDialog } from "../components/Dialog";
import { BackButton } from "../components/BackButton";
import { Settings } from "../components/Settings";
import { useOnline } from "../hooks";
import type { LibraryView } from "../library";
import { folderPath, navigate } from "../router";

type Item = { kind: "doc"; doc: DocumentMeta } | { kind: "folder"; folder: FolderMeta };

type Modal =
  | { type: "actions"; item: Item }
  | { type: "rename"; item: Item }
  | { type: "move"; item: Item }
  | { type: "new-folder" }
  | { type: "settings" }
  | null;

const nameOf = (item: Item) => (item.kind === "doc" ? item.doc.title : item.folder.name);

interface Props {
  view: LibraryView | null;
  folderId: string | null;
  refresh(): void;
  onSignOut(): void;
}

export function Library({ view, folderId, refresh, onSignOut }: Props) {
  const online = useOnline();
  const [modal, setModal] = useState<Modal>(null);
  const [error, setError] = useState<string | null>(null);
  const folder = folderId ? view?.folders.get(folderId) : undefined;
  const { folders, documents } = view?.children(folderId) ?? { folders: [], documents: [] };

  /** Runs a library change, then refreshes (other devices refresh from the broadcast). */
  async function act(fn: () => Promise<unknown>) {
    setError(null);
    setModal(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? "That didn't work. Try again." : "You're offline. Try again when connected.");
    }
    refresh();
  }

  async function newDocument() {
    setError(null);
    try {
      const doc = await api.createDocument("Untitled", folderId);
      refresh();
      navigate(`/d/${doc.id}`);
    } catch {
      setError("Couldn't create a document. Are you online?");
    }
  }

  const rename = (item: Item, name: string) =>
    act(() => (item.kind === "doc" ? api.updateDocument(item.doc.id, { title: name }) : api.updateFolder(item.folder.id, { name })));
  const move = (item: Item, to: string | null) =>
    act(() => (item.kind === "doc" ? api.updateDocument(item.doc.id, { folderId: to }) : api.updateFolder(item.folder.id, { parentId: to })));
  const trash = (item: Item) => act(() => (item.kind === "doc" ? api.trashDocument(item.doc.id) : api.trashFolder(item.folder.id)));

  // The folder was trashed or removed on another device: go to the top level.
  const gone = !!view && !!folderId && (!folder || view.folderTrashed(folderId));
  useEffect(() => {
    if (gone) navigate("/", { replace: true });
  }, [gone]);

  const crumbs = view?.path(folderId) ?? [];
  const parentId = folder?.parentId ?? null;

  return (
    <main className="library">
      <header className="library-header">
        {folderId ? (
          <BackButton
            label={parentId ? (view?.folders.get(parentId)?.name ?? "Library") : "Library"}
            onClick={() => navigate(folderPath(parentId))}
          />
        ) : (
          <span />
        )}
        <button className="quiet" onClick={() => setModal({ type: "settings" })}>
          Settings
        </button>
      </header>
      <h1>{folder ? folder.name : "Library"}</h1>
      {crumbs.length > 1 && (
        <p className="crumbs" aria-label="Folder path">
          {crumbs.map((c) => c.name).join(" / ")}
        </p>
      )}

      <div className="library-actions">
        <button className="primary" onClick={newDocument} disabled={!online}>
          New document
        </button>
        <button className="secondary" onClick={() => setModal({ type: "new-folder" })} disabled={!online}>
          New folder
        </button>
      </div>
      {!online && <p className="hint">Offline. Documents you've opened on this device still open and save.</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <ul className="item-list">
        {folders.map((f) => (
          <Row
            key={f.id}
            label={f.name}
            kind="folder"
            words={view?.folderWords(f.id)}
            onOpen={() => navigate(folderPath(f.id))}
            onMore={online ? () => setModal({ type: "actions", item: { kind: "folder", folder: f } }) : undefined}
          />
        ))}
        {documents.map((d) => (
          <Row
            key={d.id}
            label={d.title}
            kind="doc"
            words={d.wordCount ?? undefined}
            onOpen={() => navigate(`/d/${d.id}`)}
            onMore={online ? () => setModal({ type: "actions", item: { kind: "doc", doc: d } }) : undefined}
          />
        ))}
      </ul>
      {view && folders.length + documents.length === 0 && (
        <p className="hint">{folderId ? "This folder is empty." : "No documents yet."}</p>
      )}

      {!folderId && view && (
        <a
          className="trash-link"
          href="/trash"
          onClick={(e) => {
            e.preventDefault();
            navigate("/trash");
          }}
        >
          Trash{view.trashCount() ? ` (${view.trashCount()})` : ""}
        </a>
      )}

      {modal?.type === "actions" && (
        <Dialog title={nameOf(modal.item)} onClose={() => setModal(null)}>
          <div className="sheet-menu">
            <button onClick={() => setModal({ type: "rename", item: modal.item })}>Rename</button>
            <button onClick={() => setModal({ type: "move", item: modal.item })}>Move to…</button>
            <button className="danger-text" onClick={() => trash(modal.item)}>
              Move to Trash
            </button>
            <button className="quiet" onClick={() => setModal(null)}>
              Cancel
            </button>
          </div>
        </Dialog>
      )}
      {modal?.type === "rename" && (
        <PromptDialog
          title={modal.item.kind === "doc" ? "Rename document" : "Rename folder"}
          label="Name"
          initial={nameOf(modal.item)}
          confirm="Rename"
          onSubmit={(name) => rename(modal.item, name)}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "new-folder" && (
        <PromptDialog
          title="New folder"
          label="Name"
          confirm="Create"
          onSubmit={(name) => act(() => api.createFolder(name, folderId))}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "settings" && <Settings onClose={() => setModal(null)} onSignOut={onSignOut} />}
      {modal?.type === "move" && view && (
        <MoveDialog view={view} item={modal.item} onMove={(to) => move(modal.item, to)} onClose={() => setModal(null)} />
      )}
    </main>
  );
}

const formatWords = (n: number) => `${n.toLocaleString()} ${n === 1 ? "word" : "words"}`;

function Row(props: { label: string; kind: "doc" | "folder"; words?: number; onOpen(): void; onMore?: () => void }) {
  return (
    <li className={`item ${props.kind}`}>
      <button className="item-main" onClick={props.onOpen}>
        <span className="item-icon" aria-hidden>
          {props.kind === "folder" ? "▸" : ""}
        </span>
        <span className="item-label">{props.label}</span>
        {props.words !== undefined && <span className="item-words">{formatWords(props.words)}</span>}
      </button>
      {props.onMore && (
        <button className="item-more" onClick={props.onMore} aria-label={`More actions for ${props.label}`}>
          ⋯
        </button>
      )}
    </li>
  );
}

function MoveDialog({ view, item, onMove, onClose }: { view: LibraryView; item: Item; onMove(to: string | null): void; onClose(): void }) {
  const current = item.kind === "doc" ? item.doc.folderId : item.folder.parentId;
  const tree = view.folderTree(item.kind === "folder" ? item.folder.id : undefined);
  return (
    <Dialog title={`Move "${nameOf(item)}"`} onClose={onClose}>
      <div className="sheet-menu move-list">
        <button disabled={current === null} onClick={() => onMove(null)}>
          Library (top level)
        </button>
        {tree.map(({ folder, depth }) => (
          <button
            key={folder.id}
            disabled={folder.id === current}
            onClick={() => onMove(folder.id)}
            style={{ paddingLeft: `${1 + depth * 1.25}rem` }}
          >
            {folder.name}
          </button>
        ))}
        <button className="quiet" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}
